import * as EmotionTheming from 'emotion-theming'
import * as ReactRedux from 'react-redux'
import * as R from 'ramda'
import * as urql from 'urql'
import * as ReactRouter from 'react-router-dom'
import * as React from 'react'
import kebabCase from 'lodash.kebabcase'

import * as Common from '@rushplay/common'
import * as I18n from '@rushplay/i18n'

import * as configuration from './configuration'
import * as ClientConfiguration from './client-configuration'
import * as icons from './icons'
import { Divider } from './divider'
import { GameInfoModeSelector } from './game-info-mode-selector'
import { GameStatistics } from './game-statistics'
import { HtmlContent } from './html-content'
import { KewlLink } from './kewl-link'
import { NotFound } from './not-found'
import { gameInfoBackground } from './game-info-background'
import { toImageUrl } from './dynamic-image'
import { useGameInfoUrlQuery } from './use-game-info-url-query'

const getGame = `
query Game($id: ID!) {
  game(id: $id) {
      backgroundUrl
      backgroundOverlayUrl
      backgroundOverlayImageAlignment
      category
      colorMode
      displayProvider
      freePlayExcluded
      gameType
      hideRtp
      id
      imageUrl
      key
      preRelease
      provider
      tags
      title
      titleUrl
      jackpotValue
      feature
      rtp
      volatility
      biggestWinCents
      minBetCents
      maxBetCents
      dealerName
      seatsTotal
      seatsAvailable
      betBehind
      releaseDate
    }
}
`

export function GameInfo() {
  const i18n = I18n.useI18n()
  const theme = EmotionTheming.useTheme()
  const dpr = ClientConfiguration.useDevicePixelRatio()
  const gameInfoQuery = useGameInfoUrlQuery()
  const { gameId } = ReactRouter.useParams()
  const [response] = urql.useQuery({
    query: getGame,
    variables: { id: gameId },
  })
  const imgproxyUrl = ReactRedux.useSelector(state =>
    configuration.getImgproxyUrl(state.configuration)
  )
  const hasWebpSupport = ReactRedux.useSelector(state =>
    configuration.getHasWebpSupport(state.configuration)
  )

  if (response.fetching) {
    return (
      <Common.Box
        bg="rgba(0, 0, 0, 0.3)"
        display="flex"
        height="100%"
        justifyContent="center"
      >
        <Common.Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          fontSize={['48px', '64px']}
        >
          <icons.Spinner />
        </Common.Box>
      </Common.Box>
    )
  }

  if (response.error) {
    return <NotFound />
  }

  const game = R.pathOr({}, ['game'], response.data)
  const gameProvider = game.displayProvider || game.provider
  const description = `game-info.${kebabCase(gameProvider)}.${kebabCase(
    game.key
  )}.description`

  return (
    <Common.Box
      flexGrow="1"
      flexShrink="0"
      display="flex"
      flexDirection="column"
    >
      <Common.Box
        backgroundImage={[
          gameInfoBackground(
            game.backgroundUrl,
            theme,
            dpr,
            imgproxyUrl,
            'small',
            hasWebpSupport
          ),
          gameInfoBackground(
            game.backgroundUrl,
            theme,
            dpr,
            imgproxyUrl,
            'medium',
            hasWebpSupport
          ),
          gameInfoBackground(
            game.backgroundUrl,
            theme,
            dpr,
            imgproxyUrl,
            'large',
            hasWebpSupport
          ),
        ]}
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        display="flex"
        flexDirection="column"
        p={[1, 2]}
      >
        {gameInfoQuery.referrer && (
          <Common.Box alignSelf="flex-start" pb={0}>
            <KewlLink
              icon={icons.KeyboardArrowLeft}
              label="game-info.go-back"
              to={gameInfoQuery.referrerPath}
              data-testid="game-info.go-back"
            />
          </Common.Box>
        )}
        {game.titleUrl ? (
          <Common.Box
            maxWidth={['80%', '500px']}
            width="100%"
            height={['150px', '200px']}
            backgroundImage={`url(${toImageUrl(imgproxyUrl, game.titleUrl, {
              dpr,
              resizingType: 'fill',
              resizingHeight: 200,
              extension: hasWebpSupport ? 'webp' : 'png',
            })})`}
            backgroundRepeat="no-repeat"
            backgroundSize="contain"
            backgroundPosition="center"
            mx="auto"
          />
        ) : (
          <Common.Box
            fontSize={7}
            fontWeight="bold"
            color="g-text"
            textAlign="center"
            height={['150px', '200px']}
            display="flex"
            alignItems="center"
            mx="auto"
            data-testid={`game-info.game-title.${game.title}`}
            fontFamily="head"
          >
            {game.title}
          </Common.Box>
        )}
        <Common.Box display="flex" py={2} justifyContent="center">
          <GameInfoModeSelector />
        </Common.Box>
      </Common.Box>
      <Common.Box px={2}>
        <Common.Box
          display="flex"
          py={1}
          px={{ lg: 1 }}
          flexDirection="column"
          textAlign={['center', 'left']}
        >
          <Common.Box
            fontSize={6}
            fontWeight="bold"
            as="h1"
            pb={0}
            fontFamily="head"
          >
            {game.title}
          </Common.Box>
          <Common.Box
            fontSize={1}
            pb={1}
            data-testid={`game-info.game-provider.${gameProvider}`}
            fontFamily="head"
          >
            {gameProvider}
          </Common.Box>
          <Divider />
        </Common.Box>
        <Common.Box
          display="flex"
          flexDirection={['column', null, null, 'row']}
          justifyContent="space-around"
        >
          {i18n.hasTranslation(description) && (
            <Common.Box
              fontSize={2}
              mx={{ lg: 1 }}
              mt={{ lg: 0 }}
              width={{ lg: '50%' }}
            >
              <HtmlContent
                html={{ __html: i18n.translate(description) }}
                data-testid={`game-info.description.${game.title}`}
              />
            </Common.Box>
          )}
          <Common.Box
            display="flex"
            flexDirection="column"
            mx={{ lg: 1 }}
            mt={[0, null, null, 1]}
            width={{ lg: '50%' }}
          >
            <GameStatistics
              catalogType={game.gameType ? 'live-casino' : 'casino'}
              gameId={game.gameId}
              jackpotValue={game.jackpotValue}
              feature={game.feature}
              rtp={game.hideRtp ? null : game.rtp}
              volatility={game.volatility}
              biggestWinCents={game.biggestWinCents}
              minBetCents={game.minBetCents}
              maxBetCents={game.maxBetCents}
              dealerName={game.dealerName}
              seatsTotal={game.seatsTotal}
              seatsAvailable={game.seatsAvailable}
              betBehind={game.betBehind}
              releaseDate={game.releaseDate}
            />
          </Common.Box>
        </Common.Box>
      </Common.Box>
    </Common.Box>
  )
}
